"use client"

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
  type CSSProperties,
} from "react"
import { CircleDollarSign, ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"
import { usePathname } from "next/navigation"
import { trackYohoEvent } from "@/lib/analytics/track"
import {
  CURRENCY_CHANGE_EVENT,
  CURRENCY_MANUAL_OVERRIDE_KEY,
  CURRENCY_STORAGE_KEY,
  getDefaultCurrencyForLanguage,
  getSupportedCurrencyOrUsd,
  normalizeCurrencyCode,
} from "@/lib/currency-display"
import "./currency-selector.css"

// 添加 Flutter 方法类型声明
declare global {
  interface Window {
    flutterMethod?: (method: string, ...args: any[]) => void
    webappReceiveSingaporeStatus?: (
      isSingapore: boolean,
      reason: string
    ) => void
    __webappSingaporeStatus?: {
      isSingapore: boolean
      reason: string
    }
    __webappSingaporeStatusDispatcherInstalled?: boolean
  }
}

const SINGAPORE_STATUS_EVENT = "yoho:singapore-status"


const currencySelectorText: Record<string, {
  selectCurrency: string
  current: string
  changeTitle: string
  closeLabel: string
  searchPlaceholder: string
  searchLabel: string
  noMatches: string
}> = {
  en: {
    selectCurrency: "Select currency",
    current: "Current",
    changeTitle: "Click to change currency",
    closeLabel: "Close currency selector",
    searchPlaceholder: "Search currency…",
    searchLabel: "Search currency",
    noMatches: "No matching currencies",
  },
  es: {
    selectCurrency: "Seleccionar moneda",
    current: "Actual",
    changeTitle: "Haz clic para cambiar la moneda",
    closeLabel: "Cerrar selector de moneda",
    searchPlaceholder: "Buscar moneda…",
    searchLabel: "Buscar moneda",
    noMatches: "Sin monedas coincidentes",
  },
  fr: {
    selectCurrency: "Sélectionner la devise",
    current: "Actuelle",
    changeTitle: "Cliquez pour changer de devise",
    closeLabel: "Fermer le sélecteur de devise",
    searchPlaceholder: "Rechercher une devise…",
    searchLabel: "Rechercher une devise",
    noMatches: "Aucune devise correspondante",
  },
  "zh-CN": {
    selectCurrency: "选择货币",
    current: "当前",
    changeTitle: "点击切换货币",
    closeLabel: "关闭货币选择器",
    searchPlaceholder: "搜索货币…",
    searchLabel: "搜索货币",
    noMatches: "没有匹配的货币",
  },
  "zh-TW": {
    selectCurrency: "選擇貨幣",
    current: "目前",
    changeTitle: "點一下切換貨幣",
    closeLabel: "關閉貨幣選擇器",
    searchPlaceholder: "搜尋貨幣…",
    searchLabel: "搜尋貨幣",
    noMatches: "沒有符合的貨幣",
  },
  "zh-HK": {
    selectCurrency: "揀貨幣",
    current: "而家",
    changeTitle: "撳一下切換貨幣",
    closeLabel: "關閉貨幣選擇器",
    searchPlaceholder: "搜尋貨幣…",
    searchLabel: "搜尋貨幣",
    noMatches: "搵唔到符合嘅貨幣",
  },
  "ja-JP": {
    selectCurrency: "通貨を選択",
    current: "現在",
    changeTitle: "クリックして通貨を変更",
    closeLabel: "通貨選択を閉じる",
    searchPlaceholder: "通貨を検索…",
    searchLabel: "通貨を検索",
    noMatches: "一致する通貨がありません",
  },
  ko: {
    selectCurrency: "통화 선택",
    current: "현재",
    changeTitle: "클릭하여 통화 변경",
    closeLabel: "통화 선택 닫기",
    searchPlaceholder: "통화 검색…",
    searchLabel: "통화 검색",
    noMatches: "일치하는 통화 없음",
  },
}

function normalizeSelectorLocale(language: string) {
  if (language === "zh-CN" || language === "zh-TW" || language === "zh-HK" || language === "ja-JP" || language === "ko") {
    return language
  }
  if (language.startsWith("es")) return "es"
  if (language.startsWith("fr")) return "fr"
  return "en"
}

function getCurrencySelectorText(language: string) {
  const locale = normalizeSelectorLocale(language)
  const text = currencySelectorText[locale]
  if (!text) {
    if (typeof console !== "undefined") {
      console.warn(`[currency-selector] no copy for "${locale}", falling back to en`)
    }
    return currencySelectorText.en
  }
  return text
}

interface SingaporeStatusDetail {
  isSingapore: boolean
  reason: string
}

const dispatchSingaporeStatus = (detail: SingaporeStatusDetail) => {
  if (typeof window === "undefined") {
    return
  }

  window.__webappSingaporeStatus = detail
  window.dispatchEvent(
    new CustomEvent<SingaporeStatusDetail>(SINGAPORE_STATUS_EVENT, {
      detail,
    })
  )
}

const currencies = [
  {
    code: "USD",
    symbol: "$",
    name: "US Dollar",
    flag_emoji: "🇺🇸",
    regions: [
      "US",
      "AS",
      "BQ",
      "IO",
      "EC",
      "GU",
      "MH",
      "FM",
      "MP",
      "PW",
      "PR",
      "TC",
      "VI",
    ],
  },
  {
    code: "EUR",
    symbol: "€",
    name: "Euro",
    flag_emoji: "🇪🇺",
    regions: [
      "DE",
      "FR",
      "IT",
      "ES",
      "PT",
      "GR",
      "IE",
      "FI",
      "AT",
      "BE",
      "BG",
      "CY",
      "CZ",
      "DK",
      "EE",
      "HR",
      "HU",
      "LT",
      "LU",
      "LV",
      "MT",
      "NL",
      "PL",
      "RO",
      "SK",
      "SI",
      "SE",
    ],
  },
  {
    code: "JPY",
    symbol: "¥",
    name: "Japanese Yen",
    flag_emoji: "🇯🇵",
    regions: ["JP"],
  },
  {
    code: "GBP",
    symbol: "£",
    name: "British Pound",
    flag_emoji: "🇬🇧",
    regions: ["GB"],
  },
  {
    code: "CNY",
    symbol: "¥",
    name: "Chinese Yuan",
    flag_emoji: "🇨🇳",
    regions: ["CN"],
  },
  {
    code: "AUD",
    symbol: "A$",
    name: "Australian Dollar",
    flag_emoji: "🇦🇺",
    regions: ["AU"],
  },
  {
    code: "CAD",
    symbol: "C$",
    name: "Canadian Dollar",
    flag_emoji: "🇨🇦",
    regions: ["CA"],
  },
  {
    code: "CHF",
    symbol: "Fr",
    name: "Swiss Franc",
    flag_emoji: "🇨🇭",
    regions: ["CH", "LI"],
  },
  {
    code: "HKD",
    symbol: "HK$",
    name: "Hong Kong Dollar",
    flag_emoji: "🇭🇰",
    regions: ["HK"],
  },
  {
    code: "KRW",
    symbol: "₩",
    name: "South Korean Won",
    flag_emoji: "🇰🇷",
    regions: ["KR"],
  },
  {
    code: "SGD",
    symbol: "S$",
    name: "Singapore Dollar",
    flag_emoji: "🇸🇬",
    regions: ["SG"],
  },
  {
    code: "TWD",
    symbol: "NT$",
    name: "Taiwan Dollar",
    flag_emoji: "🇹🇼",
    regions: ["TW"],
  },
]

// Helper function to close mobile menu if open
const closeMobileMenuIfOpen = () => {
  const mobileMenu = document.getElementById("mobile-menu")
  // Check if menu exists and is open
  if (mobileMenu && mobileMenu.classList.contains("open")) {
    mobileMenu.classList.remove("open") // Close the menu
    document.body.style.overflow = "" // Restore body scroll
    const menuToggleIcon = document.querySelector("#menu-toggle span")
    if (menuToggleIcon) {
      menuToggleIcon.textContent = "≡" // Reset toggle icon
    }
    document.dispatchEvent(new CustomEvent("yoho:mobile-menu-close"))
  }
}

interface CurrencySelectorProps {
  currency: string
  onCurrencyChange?: (currency: string) => void
  showEmoji?: boolean
  variant?: "compact" | "row"
}

export const CurrencySelector = ({
  currency: initialCurrency,
  onCurrencyChange,
  showEmoji = true,
  variant = "compact",
}: CurrencySelectorProps) => {
  const isRow = variant === "row"
  const { t, i18n } = useTranslation()
  const pathname = usePathname()
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [currency, setCurrency] = useState(
    getSupportedCurrencyOrUsd(initialCurrency)
  )
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const [dialogStyle, setDialogStyle] = useState<CSSProperties>({})
  // Ref to read current currency inside effects without adding it as a dep (would cause infinite loop)
  const currencyRef = useRef(currency)
  useEffect(() => {
    currencyRef.current = currency
  }, [currency])
  const [isDisabled, setIsDisabled] = useState(false)
  const [disableReason, setDisableReason] = useState<string>("")
  const [isSingapore, setIsSingapore] = useState<boolean>(false)

  useEffect(() => {
    if (typeof window === "undefined") return
    if (localStorage.getItem("yoho-sg-detected") === "1") {
      setIsSingapore(true)
    }
  }, [])

  const updateDialogPosition = useCallback(() => {
    if (typeof window === "undefined" || !triggerRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const margin = viewportWidth <= 480 ? 12 : 16
    const panelWidth = Math.min(420, viewportWidth - margin * 2)
    const preferredLeft = triggerRect.right - panelWidth
    const maxLeft = Math.max(margin, viewportWidth - panelWidth - margin)
    const left = Math.min(Math.max(preferredLeft, margin), maxLeft)
    const top = Math.min(
      triggerRect.bottom + 10,
      Math.max(12, viewportHeight - 94)
    )
    const arrowLeft = Math.min(
      Math.max(triggerRect.left + triggerRect.width / 2 - left, 28),
      panelWidth - 28
    )

    setDialogStyle({
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      width: `${Math.round(panelWidth)}px`,
      "--dialog-arrow-left": `${Math.round(arrowLeft)}px`,
    } as CSSProperties & Record<"--dialog-arrow-left", string>)
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) return
    updateDialogPosition()
  }, [isOpen, updateDialogPosition])

  useEffect(() => {
    if (!isOpen) return
    const handleReposition = () => updateDialogPosition()
    window.addEventListener("resize", handleReposition)
    window.addEventListener("scroll", handleReposition, true)
    return () => {
      window.removeEventListener("resize", handleReposition)
      window.removeEventListener("scroll", handleReposition, true)
    }
  }, [isOpen, updateDialogPosition])

  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const handlePointer = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false)
    }
    document.addEventListener("pointerdown", handlePointer)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener("pointerdown", handlePointer)
      document.removeEventListener("keydown", handleKey)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) setSearchQuery("")
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !listRef.current) return
    const node = listRef.current.querySelector<HTMLElement>(
      `[data-currency-code="${currency}"]`
    )
    if (!node) return
    const list = listRef.current
    const itemTop = node.offsetTop
    const itemBottom = itemTop + node.offsetHeight
    const visibleTop = list.scrollTop
    const visibleBottom = visibleTop + list.clientHeight

    if (itemTop < visibleTop) {
      list.scrollTop = Math.max(0, itemTop - 8)
    } else if (itemBottom > visibleBottom) {
      list.scrollTop = itemBottom - list.clientHeight + 8
    }
  }, [isOpen, currency])

  // 检查是否在 Flutter 环境中
  const isFlutterEnvironment =
    typeof window !== "undefined" && window.flutterMethod

  const applyCurrency = useCallback(
    (
      nextCurrency: string,
      options?: {
        closeDialog?: boolean
        manualOverride?: boolean
        persist?: boolean
      }
    ) => {
      const normalizedCurrency = getSupportedCurrencyOrUsd(nextCurrency)
      setCurrency(normalizedCurrency)
      onCurrencyChange?.(normalizedCurrency)

      if (typeof window !== "undefined" && options?.persist !== false) {
        localStorage.setItem(CURRENCY_STORAGE_KEY, normalizedCurrency)
        if (typeof options?.manualOverride === "boolean") {
          localStorage.setItem(
            CURRENCY_MANUAL_OVERRIDE_KEY,
            options.manualOverride ? "1" : "0"
          )
        }
      }

      if (options?.closeDialog) {
        setIsOpen(false)
      }
    },
    [onCurrencyChange]
  )

  const handleSingaporeStatus = useCallback(
    (isSingaporeResult: boolean, reason: string) => {
      console.log(
        `[Webapp] RECEIVED Singapore status from Flutter: ${isSingaporeResult}, reason: ${reason}`
      )

      setIsSingapore(isSingaporeResult)

      if (isSingaporeResult) {
        console.log(
          "[Webapp] Singapore detected via Flutter callback, immediately switching to SGD and changing UI structure"
        )
        localStorage.setItem("yoho-sg-detected", "1")
        applyCurrency("SGD", { closeDialog: true, manualOverride: false })

        if (
          typeof window !== "undefined" &&
          typeof window.flutterMethod === "function"
        ) {
          window.flutterMethod("switchCurrency", "SGD")
        }
      }
    },
    [applyCurrency]
  )

  // 设置回调函数接收 Flutter 的新加坡状态
  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    console.log("[Webapp] Setting up Singapore status listener")
    console.log(
      "[Webapp] Current window object keys:",
      Object.keys(window).filter(
        (key) => key.includes("webapp") || key.includes("Singapore")
      )
    )

    const handleSingaporeStatusEvent = (event: Event) => {
      const detail = (event as CustomEvent<SingaporeStatusDetail>).detail
      handleSingaporeStatus(detail.isSingapore, detail.reason)
    }

    window.addEventListener(SINGAPORE_STATUS_EVENT, handleSingaporeStatusEvent)

    if (!window.__webappSingaporeStatusDispatcherInstalled) {
      window.webappReceiveSingaporeStatus = (
        isSingaporeResult: boolean,
        reason: string
      ) => {
        dispatchSingaporeStatus({ isSingapore: isSingaporeResult, reason })
      }
      window.__webappSingaporeStatusDispatcherInstalled = true
      console.log("[Webapp] Installed global Singapore status dispatcher")
    } else {
      console.log(
        "[Webapp] Reusing existing global Singapore status dispatcher"
      )
    }

    const cachedStatus = window.__webappSingaporeStatus
    if (cachedStatus) {
      console.log(
        "[Webapp] Replaying cached Singapore status for current selector:",
        cachedStatus
      )
      handleSingaporeStatus(cachedStatus.isSingapore, cachedStatus.reason)
    }

    console.log("[Webapp] Final window properties related to callback:", {
      hasWebappCallback: "webappReceiveSingaporeStatus" in window,
      callbackType: typeof window.webappReceiveSingaporeStatus,
      hasFlutterMethod: "flutterMethod" in window,
      flutterMethodType: typeof window.flutterMethod,
      hasCachedSingaporeStatus: Boolean(window.__webappSingaporeStatus),
    })

    if (isFlutterEnvironment) {
      console.log("[Webapp] Requesting Singapore status from Flutter")
      window.flutterMethod?.("requestSingaporeStatus")
    }

    return () => {
      window.removeEventListener(
        SINGAPORE_STATUS_EVENT,
        handleSingaporeStatusEvent
      )
    }
  }, [handleSingaporeStatus, isFlutterEnvironment])

  // 切换货币的方法
  const switchCurrency = (code: string) => {
    if (isFlutterEnvironment) {
      // 检查是否真的需要切换货币，避免无意义的调用
      if (code.toLowerCase() === currency.toLowerCase()) {
        console.log(
          `[Currency Selector] Currency already set to ${code}, skipping Flutter call`
        )
        return
      }
      // 在 Flutter 环境中，调用 Flutter 方法
      console.log(
        `[Currency Selector] Calling Flutter switchCurrency: ${currency} -> ${code}`
      )
      window.flutterMethod?.("switchCurrency", code)
    }
  }

  // 检查是否应该禁用货币选择器
  useEffect(() => {
    const checkShouldDisable = () => {
      // 检查是否在结账相关页面
      const checkoutPaths = ["/checkout", "/payment", "/paynow"]
      const isInCheckoutPath = checkoutPaths.some((path) =>
        pathname?.toLowerCase().includes(path.toLowerCase())
      )

      console.log(
        `[Currency Selector] Disable check - pathname: ${pathname}, isInCheckoutPath: ${isInCheckoutPath}, isSingapore: ${isSingapore}`
      )

      const shouldDisable = isInCheckoutPath || isSingapore

      // 设置禁用原因
      let reason = ""
      if (isInCheckoutPath && isSingapore) {
        reason =
          "Currency switching is disabled during checkout and in Singapore region"
      } else if (isInCheckoutPath) {
        reason = "Currency switching is disabled during checkout"
      } else if (isSingapore) {
        reason = "Singapore users must use SGD currency"
      }

      if (shouldDisable) {
        console.log(
          `[Currency Selector] Currency selector disabled. Reason: ${reason}`
        )
      } else {
        console.log(`[Currency Selector] Currency selector enabled`)
      }

      setIsDisabled(shouldDisable)
      setDisableReason(reason)
    }

    checkShouldDisable()
  }, [pathname, isSingapore])

  // 当 SG 模式激活时（含从 localStorage 恢复），确保货币为 SGD
  useEffect(() => {
    if (!isSingapore || typeof window === "undefined") return
    applyCurrency("SGD", { manualOverride: false })
  }, [isSingapore, applyCurrency])

  // 页面加载和语言变化时处理货币设置
  useEffect(() => {
    // 如果已经检测到新加坡，跳过初始化
    if (isSingapore) {
      return
    }

    // 检查是否在结账页面，如果是则不要同步到 Flutter（避免触发限制检查）
    const checkoutPaths = ["/checkout", "/payment", "/paynow"]
    const isInCheckoutPath = checkoutPaths.some((path) =>
      pathname?.toLowerCase().includes(path.toLowerCase())
    )

    console.log(
      `[Currency Selector] Currency initialization - pathname: ${pathname}, isInCheckoutPath: ${isInCheckoutPath}, isFlutterEnvironment: ${isFlutterEnvironment}`
    )

    const defaultCurrency = getDefaultCurrencyForLanguage(i18n.language)
    const savedCurrency = localStorage.getItem(CURRENCY_STORAGE_KEY)
    const hasManualOverride =
      localStorage.getItem(CURRENCY_MANUAL_OVERRIDE_KEY) === "1"
    const nextCurrency =
      hasManualOverride && savedCurrency
        ? getSupportedCurrencyOrUsd(savedCurrency)
        : defaultCurrency

    applyCurrency(nextCurrency, { manualOverride: hasManualOverride })

    if (isFlutterEnvironment && !isInCheckoutPath) {
      if (normalizeCurrencyCode(currencyRef.current) !== nextCurrency) {
        console.log(
          `[Currency Selector] Calling Flutter switchCurrency: ${currencyRef.current} -> ${nextCurrency}`
        )
        window.flutterMethod?.("switchCurrency", nextCurrency)
      } else {
        console.log(
          `[Currency Selector] Currency already matches target (${nextCurrency}), skipping Flutter call`
        )
      }
    }
  }, [
    applyCurrency,
    i18n.language,
    isFlutterEnvironment,
    isSingapore,
    pathname,
  ])

  const currentCurrency =
    currencies.find((c) => c.code === currency) || currencies[0]
  const currentCurrencyLabel = `${currentCurrency.name}, ${currentCurrency.symbol} ${currentCurrency.code}`

  useEffect(() => {
    if (typeof window === "undefined" || !currency) {
      return
    }

    window.dispatchEvent(
      new CustomEvent(CURRENCY_CHANGE_EVENT, {
        detail: { currency },
      })
    )
  }, [currency])

  const handleCurrencyChange = (code: string) => {
    if (isDisabled) {
      return // 禁用时不执行任何操作
    }

    const normalizedCurrency = getSupportedCurrencyOrUsd(code)
    trackYohoEvent("currency_change", {
      previous_currency: currency,
      next_currency: normalizedCurrency,
    })
    applyCurrency(normalizedCurrency, {
      closeDialog: true,
      manualOverride: true,
    })
    // 调用切换货币的方法
    switchCurrency(normalizedCurrency)

    // Close mobile menu if it was open
    closeMobileMenuIfOpen()
  }

  const handleTriggerClick = () => {
    if (isDisabled) {
      return // 禁用时不打开选择器
    }
    setIsOpen(true)
  }

  // 如果检测到新加坡，直接显示 SGD，不提供选择功能
  if (isSingapore) {
    console.log(
      "[Webapp] React structure changed: Rendering fixed SGD display (Singapore mode)"
    )
    const sgdCurrency =
      currencies.find((c) => c.code === "SGD") || currencies[0]
    return (
      <div className="currency-selector">
        <div
          className="currency-trigger-fixed"
          aria-label={`当前货币：${sgdCurrency.name}, ${sgdCurrency.symbol} ${sgdCurrency.code}`}
        >
          {sgdCurrency && (
            <>
              <CircleDollarSign className="currency-nav-icon" aria-hidden="true" strokeWidth={2.2} />
              <span className="currency-code">
                {sgdCurrency.code}
              </span>
            </>
          )}
        </div>
      </div>
    )
  }

  console.log(
    "[Webapp] React structure: Rendering normal currency selector (non-Singapore mode)"
  )
  const currencyText = getCurrencySelectorText(i18n.language)
  const selectCurrencyLabel = currencyText.selectCurrency
  const currentCurrencyAriaLabel = currencyText.current
  const changeCurrencyTitle = currencyText.changeTitle
  const closeCurrencyLabel = currencyText.closeLabel
  const searchCurrencyPlaceholder = currencyText.searchPlaceholder
  const searchCurrencyLabel = currencyText.searchLabel
  const currencyNoMatches = currencyText.noMatches

  return (
    <div
      className={`currency-selector${isRow ? " currency-selector-row" : ""}`}
      ref={containerRef}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`currency-trigger${isRow ? " currency-trigger-row" : ""} ${isDisabled ? "disabled" : ""}`}
        onClick={handleTriggerClick}
        style={isDisabled ? { cursor: "not-allowed", opacity: 0.6 } : {}}
        title={isDisabled ? disableReason : changeCurrencyTitle}
        aria-label={`${selectCurrencyLabel}, ${currentCurrencyAriaLabel} ${currentCurrencyLabel}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        disabled={isDisabled}
      >
        {currentCurrency && (
          <>
            <CircleDollarSign className="currency-nav-icon" aria-hidden="true" strokeWidth={2.2} />
            {isRow ? (
              <>
                <span className="currency-name-row">
                  {currentCurrency.name} · {currentCurrency.symbol} {currentCurrency.code}
                </span>
                <ChevronRight
                  className="currency-chevron"
                  aria-hidden="true"
                  strokeWidth={2}
                />
              </>
            ) : (
              <span className="currency-code">{currentCurrency.code}</span>
            )}
          </>
        )}
      </button>

      {isOpen && !isDisabled && (
        <div
          className="currency-dialog"
          style={dialogStyle}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) setIsOpen(false)
          }}
        >
          <div
            className="currency-dialog-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="currency-dialog-title"
          >
            <div className="currency-dialog-header">
              <h3 id="currency-dialog-title">{selectCurrencyLabel}</h3>
              <button
                type="button"
                className="close-button"
                aria-label={closeCurrencyLabel}
                onClick={() => setIsOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="search-container">
              <input
                type="search"
                className="search-input"
                placeholder={searchCurrencyPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label={searchCurrencyLabel}
              />
            </div>

            <div className="currency-list" ref={listRef}>
              {(() => {
                const q = searchQuery.trim().toLowerCase()
                const filtered = q
                  ? currencies.filter(
                      (c) =>
                        c.code.toLowerCase().includes(q) ||
                        c.name.toLowerCase().includes(q)
                    )
                  : currencies
                if (q && filtered.length === 0) {
                  return (
                    <div className="currency-empty">
                      {currencyNoMatches}
                    </div>
                  )
                }
                return filtered.map((curr) => (
                <button
                  type="button"
                  key={curr.code}
                  data-currency-code={curr.code}
                  className={`currency-item ${curr.code === currency ? "selected" : ""}`}
                  aria-pressed={curr.code === currency}
                  onClick={() => handleCurrencyChange(curr.code)}
                >
                  {showEmoji && (
                    <span className="currency-emoji noto-color-emoji-regular">
                      {curr.flag_emoji}
                    </span>
                  )}
                  <span className="currency-name">
                    <span className="currency-primary">
                      {curr.symbol} {curr.code}
                    </span>
                    <span className="currency-secondary">{curr.name}</span>
                  </span>
                </button>
                ))
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
